import React, { useState, useRef, useEffect } from 'react';
import { 
  Calendar, 
  Users, 
  CheckCircle, 
  CreditCard, 
  MessageSquare,
  Shield,
  File,
  Mail,
  Bell,
  User,
  Inbox,
  LogOut,
  ChevronDown
} from 'lucide-react';
import Logo from '../../../../components/Logo';

const iconMap = {
  Calendar,
  Users,
  CheckCircle,
  CreditCard,
  MessageSquare,
  Shield,
  File,
  Inbox
};

const TopNavBar = ({ tabs, activeTab, onTabChange, user, onLogout }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileRef]);

  return (
    <div className="bg-white border-b-2 border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-6 py-3">
        {/* Logo Section */}
        <div className="flex items-center min-w-[140px]">
          <Logo size="sm" showText={true} />
        </div>

        {/* Navigation Tabs - Centered */}
        <nav className="flex items-center bg-gray-100 rounded-full p-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`
                  px-4 py-2 text-lg font-medium transition-all duration-200 rounded-full whitespace-nowrap
                  ${isActive 
                    ? 'bg-[#2D869C] text-white shadow-sm' 
                    : 'text-gray-600 hover:text-[#2D869C] hover:bg-gray-200'
                  }
                `}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Right Side - Icons and User Profile */}
        <div className="flex items-center space-x-2 min-w-[140px] justify-end">
          {/* Mail Icon */}
          <button className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors">
            <Mail size={18} className="text-gray-600" />
          </button>

          {/* Notifications */}
          <button className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors relative">
            <Bell size={18} className="text-gray-600" />
          </button>

          {/* User Profile Dropdown */}
          <div className="relative" ref={profileRef}>
            <button 
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center focus:outline-none"
            >
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#2D869C] shadow-sm">
                {user?.avatar ? (
                  <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-orange-100 flex items-center justify-center text-orange-600">
                    <User size={18} />
                  </div>
                )}
              </div>
            </button>

            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border-2 border-gray-200 py-2 z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                <div className="px-5 py-4 border-b-2 border-gray-200 bg-gray-50">
                  <p className="text-lg font-bold text-gray-900 truncate">{user?.name || 'User'}</p>
                  <p className="text-md text-gray-600 truncate font-medium mt-1">{user?.email || 'No email'}</p>
                  {user?.role && (
                    <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-[#9DC9D4]/30 text-[#2D869C]">
                      {user.role}
                    </div>
                  )}
                </div>
                <div className="py-2">
                  <button 
                    onClick={() => {
                      onTabChange('profile-management');
                      setIsProfileOpen(false);
                    }} 
                    className="w-full text-left px-5 py-2.5 text-md text-gray-700 hover:bg-gray-50 flex items-center transition-colors font-medium"
                  >
                    <User size={18} className="mr-3 text-gray-500" />
                    My Profile
                  </button>
                  <button 
                    onClick={onLogout}
                    className="w-full text-left px-5 py-2.5 text-md text-red-600 hover:bg-red-50 flex items-center transition-colors font-semibold border-t-2 border-gray-200 mt-1 pt-3"
                  >
                    <LogOut size={18} className="mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default TopNavBar;
